import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "shared/components/UIElements/LoadingSpinner";
import { getCurrentLanguage } from "shared/utility/lang";

import Card from "shared/components/UIElements/Card";
import GenericError from "shared/components/UIElements/GenericError";
import Input from "shared/components/FormElements/Input";
import Button from "shared/components/FormElements/Button";
import Link from "shared/components/FormElements/Link";
import { VALIDATOR_MINLENGTH, VALIDATOR_EMAIL } from "shared/util/validators";
import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";

import { getDeviceId } from "shared/util/auth/device-id";
import { isInternationalTheme, getThemeName } from "config/ConfigUtils";
import { trackSuiteEvent } from "shared/util/track";
import { createAndLoginDemoAccount } from "features/start/demo-session";
import BookDemoSection from "./components/BookDemoSection";
import "./Auth.css";

const Auth = () => {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const [genericError, setGenericError] = useState(null);
  const { isLoading, sendRequest } = useHttpClient();
  const [loginFailed, setLoginFailed] = useState(false);
  const [failedMessage1, setFailedMessage1] = useState("Error");
  const [failedMessage2, setFailedMessage2] = useState("");
  const [isDemoLoading, setIsDemoLoading] = useState(false);
  const [isLoginLoading, setIsLoginLoading] = useState(false);

  const [formState, inputHandler] = useForm(
    {
      email: {
        value: "",
        isValid: false,
      },
      password: {
        value: "",
        isValid: false,
      },
    },
    false,
  );

  useEffect(() => {
    if (localStorage.getItem("GenericError")) {
      setGenericError(localStorage.getItem("GenericError"));
      localStorage.removeItem("GenericError");
    }
  }, [auth]);

  const onHandleInput = (event) => {
    event.preventDefault();
    setLoginFailed(false);
  };

  const authSubmitHandler = async (event) => {
    event.preventDefault();

    let language = getCurrentLanguage();

    const deviceId = getDeviceId();
    setIsLoginLoading(true);
    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/clients/login`,
        "POST",
        JSON.stringify({
          phonenumber: formState.inputs.email.value,
          password: formState.inputs.password.value,
          language: language,
          deviceId: deviceId,
        }),
        {
          "Content-Type": "application/json",
        },
      );

      await auth.login(responseData.id, responseData.token);
    } catch (err) {
      const firstDotIndex = err?.message?.indexOf(".");
      if (firstDotIndex !== -1 && err.code === 403) {
        setFailedMessage1(err?.message?.slice(0, firstDotIndex + 1));
        setFailedMessage2(err?.message?.slice(firstDotIndex + 1).trim());
      } else {
        setFailedMessage1(err?.message);
        setFailedMessage2("");
      }
      document.getElementById("email").focus();
      setLoginFailed(true);
    } finally {
      setIsLoginLoading(false);
    }
  };

  const loginWithDemoHandler = async () => {
    trackSuiteEvent("Login — Try a Live Demo", "button_click");
    setLoginFailed(false);
    setIsDemoLoading(true);

    try {
      await createAndLoginDemoAccount({
        sendRequest,
        login: auth.login,
      });
    } catch (err) {
      setFailedMessage1(err?.message || t("auth.demoLoginFailed"));
      setFailedMessage2("");
      setLoginFailed(true);
    } finally {
      setIsDemoLoading(false);
    }
  };

  if (!auth.loaded) {
    return <LoadingSpinner asOverlay />;
  }

  return (
    <React.Fragment>
      {genericError && <GenericError text={genericError} />}

      <div className="auth-page-container">
        <Card className="authentication">
          <h2 style={{ marginTop: "5px" }}>{t("userLogin")}</h2>
          <hr />
          <form onSubmit={authSubmitHandler}>
            <Input
              element="input"
              id="email"
              type="email"
              label={t("email")}
              validators={[VALIDATOR_EMAIL()]}
              onInput={inputHandler}
              onChange={onHandleInput}
              disabled={isLoading || isDemoLoading}
              autoComplete={"username"}
              dontShowError={true}
            />
            <Input
              element="input"
              id="password"
              type="password"
              label={t("password")}
              validators={[VALIDATOR_MINLENGTH(6)]}
              onInput={inputHandler}
              onChange={onHandleInput}
              disabled={isLoading || isDemoLoading}
              autoComplete={"current-password"}
              dontShowError={true}
            />
            <Button
              type="submit"
              isLoading={isLoginLoading}
              disabled={!formState.isValid || isLoading || isDemoLoading}
            >
              {t("login")}
            </Button>

            {loginFailed && (
              <div className="login-failed">
                <p>{failedMessage1}</p>
                <p>{failedMessage2}</p>
              </div>
            )}

            <div className="auth-forgot-password">
              <Link to="/reset-password">{t("auth.forgotPassword")}</Link>
            </div>

            {isInternationalTheme() && (
              <>
                <hr className="auth-divider" />

                <div className="auth-alt-actions">
                  <div className="auth-alt-action">
                    <span className="auth-alt-label">{t("auth.newToBrand", { brand: getThemeName() })}</span>
                    <button
                      type="button"
                      className="auth-alt-link"
                      onClick={loginWithDemoHandler}
                      disabled={isDemoLoading}
                    >
                      → {isDemoLoading ? t("auth.loading") : t("auth.tryLiveDemo")} ({t("auth.noSignupRequired")})
                    </button>
                  </div>

                  <div className="auth-alt-action">
                    <span className="auth-alt-label">{t("auth.dontHaveAccount")}</span>
                    <Link
                      to="/sign-up"
                      className="auth-alt-link"
                      styles={{ fontWeight: "bold", marginTop: "0px" }}
                      onClick={() => trackSuiteEvent("Login — Create Account", "button_click")}
                    >
                      → {t("auth.createAccount")}
                    </Link>
                  </div>

                  <div className="auth-alt-action">
                    <span className="auth-alt-label">Prefer to look around first?</span>
                    <Link
                      to="/start"
                      className="auth-alt-link"
                      styles={{ fontWeight: "bold", marginTop: "0px" }}
                      onClick={() => trackSuiteEvent("Login — Explore Start", "button_click")}
                    >
                      → Explore with a spreadsheet first
                    </Link>
                  </div>
                </div>
              </>
            )}
          </form>
        </Card>

        {/* Book a Demo Call Section */}
        {isInternationalTheme() && <BookDemoSection eventName="Login — Book a Demo Call" />}
      </div>
    </React.Fragment>
  );
};

export default Auth;
